import React, { useState, useEffect, useRef, useContext } from "react";
import Helmet from "react-helmet";
import { Header, Body, Footer, Isi } from "./layout-blocks";
import { appContext } from "providers/appProvider";
import { useCookies } from "react-cookie";
import "scss/default-theme/theme.scss";
import favIcon from "images/favicon.png";
import PropTypes from "prop-types";
import { Modal } from "../simple-overlay";
import { navigate } from "gatsby";
import '@fontsource-variable/montserrat';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/montserrat/900.css';
import ContentModal from "../content-modal";
import NavBar from "../nav-bar";

export default function Layout({
  indication,
  noShowStickyIsi,
  pageClass,
  breadcrumbData,
  containerClass,
  hero,
  children,
  reference,
  className,
  ...props }) {
  const {
    updateCurrentIndication,
    appConfigs,
  } = useContext(appContext);
  const footerRef = useRef(null);
  const [elmRef, setElmRef] = useState(null);
  const [showHcpModal, setShowHcpModal] = useState(false);
  const [cookies, setCookie] = useCookies(["hcp-site-visited"]);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);

  useEffect(() => {
    const handleInteraction = (e) => {
      switch (e.type) {
        case 'keydown':
          if (e.key === 'Tab') {
            document.body.classList.add('user-is-tabbing');
          }
          break;
        case 'mousedown':
          document.body.classList.remove('user-is-tabbing');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleInteraction);
    document.addEventListener('mousedown', handleInteraction);

    return () => {
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('mousedown', handleInteraction);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        const okButton = document.querySelector('.cc-btn.cc-allow');
        if (document.activeElement === okButton) {
          okButton.click();
          // Attempt to focus the expand button immediately after the modal is dismissed
          setTimeout(() => {
            const expandButton = document.querySelector('.btn-expand-isi');
            if (expandButton) {
              expandButton.focus();
            } else {
              console.log('Expand button not found.');
            }
          }, 0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);


  const isHcpCookieSet = () => {
    const cookieFlag = cookies["hcp-site-visited"];
    const isPatientPage = window.location.pathname === "/patient";
    if (!cookieFlag && !isPatientPage) {
      setShowHcpModal(true);
    }
    return cookieFlag;
  };


  const handlePatientClick = () => {
    document.referrer !== "" ? window.history.back() : navigate("/patient/");
    setShowHcpModal(false);
  };

  const handleHcpClick = () => {
    setCookie("hcp-site-visited", "visited", { path: "/" });
    setShowHcpModal(false);
  };

  const handleSetCookie = () => {
    setCookie('HCP_modal_clicked', true, {
      maxAge: 2592000
    })
  }



  useEffect(() => {
    setElmRef(footerRef.current);
    updateCurrentIndication(indication);
    // if (indication === "hcp") {
    //   isHcpCookieSet();
    // }
  }, [indication, isHcpCookieSet, updateCurrentIndication]);


  useEffect(() => {
  const focusCookieConsent = () => {
    // Correctly escape the colon in the ID selector
    const consentLink = document.querySelector('#cookieconsent\\:desc .cc-link');
    if (consentLink) {
      consentLink.focus();
    }
  };

  // Delay to allow third-party scripts to load and render the consent banner
  const timer = setTimeout(focusCookieConsent, 1000); // Adjust the delay as needed

  return () => clearTimeout(timer);
}, []);

// State to track the dismissal of the cookie consent modal
const [cookieConsentDismissed, setCookieConsentDismissed] = useState(false);

useEffect(() => {
  // Your existing logic for handling the first tab interaction and setting up initial focus
}, []);

useEffect(() => {
  // Logic to handle the cookie consent focus and dismissal
  // and then set 'cookieConsentDismissed' to true
  const consentLink = document.querySelector('#cookieconsent\\:desc .cc-link');
  if (consentLink) {
    consentLink.focus();
  }

  // Example logic to set cookieConsentDismissed based on some event or user action
  // setCookieConsentDismissed(true);
}, []);

useEffect(() => {
    if (cookieConsentDismissed) {
      // Select the sticky ISI plus button using its class name or id
      const isiButton = document.querySelector('.btn-expand-isi');
      if (isiButton) {
        // Set a timeout to ensure the focus occurs after the DOM has been updated
        setTimeout(() => {
          isiButton.focus(); // Focus the sticky ISI plus button
        }, 0);
      }
    }
  }, [cookieConsentDismissed]);
  
  return (
    <>
      <Helmet htmlAttributes={{
        lang: 'en', // use the appropriate language code here
      }}>
        <link rel="icon" href={favIcon} />
      </Helmet>
      <div {...props} className={`${pageClass ? pageClass : ""} site-wrapper`}>
        <Header />
        <Body style={{ paddingTop: `${100}px` }}>
        <NavBar isKeyboardNav={isKeyboardNav} />
          {children}
        </Body>
        <Modal
          shouldOpen={showHcpModal}
          modalTitle={appConfigs.hcpModalInfo.TITLE}
          modalBody={appConfigs.hcpModalInfo.BODY}
          className={appConfigs.hcpModalInfo.CLASS_NAME}
          buttonOneTitle={appConfigs.hcpModalInfo.BUTTON_ONE_CAPTION}
          buttonTwoTitle={appConfigs.hcpModalInfo.BUTTON_TWO_CAPTION}
          onButtonOneHandler={handlePatientClick}
          onButtonTwoHandler={handleHcpClick}
        >
        </Modal>
        <ContentModal initiallyOpen={!cookies.HCP_modal_clicked} onOkClicked={handleSetCookie}>
          <p>This information is intended for US healthcare professionals only.</p>
          <p>By clicking OK below, you confirm that you are a US healthcare professional.</p>
        </ContentModal>
        {elmRef && !noShowStickyIsi && <Isi footerRef={elmRef} reference={reference} />}
        <div ref={footerRef}>
          <Footer />
        </div>
      </div>
    </>
  );
}

Layout.propTypes = {
  indication: PropTypes.oneOf(["hcp", "patient"]).isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  noShowStickyIsi: PropTypes.string,
  hasReference: PropTypes.bool,
};

